/** Four complete photography-brief systems. Templates stamp editable content and geometry into a document. */
import blueHorizonAsset from "@/assets/brief-blue-horizon.jpg.asset.json";
import burnishedMotionAsset from "@/assets/brief-burnished-motion.jpg.asset.json";
import nocturneAsset from "@/assets/brief-nocturne.jpg.asset.json";
import sleekMonochromeAsset from "@/assets/brief-sleek-monochrome.jpg.asset.json";
import locationStudioAsset from "@/assets/location-studio.jpg.asset.json";
// Kept only so older saved briefs retain their original placeholder images.
import legacyRedline from "@/assets/brief-template-redline.jpg";
import legacyTidal from "@/assets/brief-template-tidal.jpg";
import legacyBeauty from "@/assets/brief-template-beauty.jpg";
import legacyStudio from "@/assets/brief-template-studio.jpg";
import {
  VISUAL_SECTIONS,
  defaultDesign,
  kindDef,
  newId,
  type AreaId,
  type BriefDesign,
  type BriefDoc,
  type BriefKind,
  type ElementLayout,
  type PageLayoutMap,
  type PageTheme,
  type ReferenceCrop,
  type SectionType,
} from "./model";
import { PHOTOGRAPHY_TEMPLATE_PAGES, templatePageRecipe, templatePageTheme, type TemplatePageKey } from "./pageRecipes";

// Managed assets use Focus's own CDN route. The absolute origin also keeps
// them visible inside local/editor previews where that route is not proxied.
const focusAsset = (url: string) => url.startsWith("/__l5e/") ? `https://focuscasting.com${url}` : url;

export type TemplatePreviewStyle = "horizon" | "motion" | "nocturne" | "monochrome" | "blank";

export interface TemplateSample {
  title: string;
  subtitle: string;
  idea: string;
  imageUrl: string;
  assetKey: string;
  previewStyle: TemplatePreviewStyle;
  sectionCopy: Partial<Record<SectionType, string>>;
  sectionTitles?: Partial<Record<SectionType, string>>;
  intro?: string;
  project?: { client: string; project: string; dates: string; location: string };
}

export interface BriefTemplate {
  id: string;
  label: string;
  blurb: string;
  kind: BriefKind;
  areas: AreaId[];
  sections: SectionType[];
  design: Partial<BriefDesign> & { fontPair?: string };
  sample?: TemplateSample;
  custom?: boolean;
}

export interface EditorialSystem {
  id: Exclude<TemplatePreviewStyle, "blank">;
  eyebrow: string;
  accent: string;
  muted: string;
  coverTone: "light" | "dark";
}

export const EDITORIAL_SYSTEMS: Record<string, EditorialSystem> = {
  blue_horizon: { id: "horizon", eyebrow: "BLUE HORIZON / PHOTOGRAPHY", accent: "#4A8CCB", muted: "#E6F2FA", coverTone: "light" },
  burnished_motion: { id: "motion", eyebrow: "BURNISHED MOTION / CAMPAIGN", accent: "#B65B32", muted: "#E8D2B7", coverTone: "dark" },
  nocturne: { id: "nocturne", eyebrow: "NOCTURNE / IMAGE TREATMENT", accent: "#A9784E", muted: "#4A3123", coverTone: "dark" },
  sleek_monochrome: { id: "monochrome", eyebrow: "SLEEK / PHOTOGRAPHY BRIEF", accent: "#17171A", muted: "#E7E7E7", coverTone: "light" },
};

export const PHOTOGRAPHY_BRIEF_SECTIONS: SectionType[] = [
  "collection", "references", "photography", "styling", "hair", "makeup", "casting", "production", "schedule", "deliverables", "usage", "contact",
];

const placed = (x: number, y: number, width: number, height: number, zIndex = 1): ElementLayout => ({ x, y, width, height, rotation: 0, zIndex });
const crop = (x: number, y: number, zoom = 1): ReferenceCrop => ({ x, y, zoom, ratio: "landscape", fit: "fill" });

const sectionCopy: Partial<Record<SectionType, string>> = {
  references: "A concise image vocabulary for light, color, composition and movement.",
  casting: "Distinctive presence, assured movement and a natural ability to hold a quiet frame.",
  photography: "Prioritize shape, gesture and atmosphere. Frames should feel observed rather than over-directed.",
  styling: "Build a controlled wardrobe story through silhouette, texture and a disciplined color edit.",
  collection: "Spring / Summer 26\nThis collection is shaped by calm silhouettes, tactile structure and an exact monochrome palette.",
  hair: "Modern, tactile and intentional. Preserve natural character while keeping every finish camera-ready.",
  makeup: "Refined skin, considered detail and tonal balance that supports the light rather than competing with it.",
  production: "A location with strong natural geometry, clean access and enough depth to create several distinct worlds.",
  schedule: "08:00 Crew call\n09:00 Hair, makeup and wardrobe\n10:30 First frame\n13:00 Lunch\n14:00 Look two\n17:00 Hero image\n18:30 Wrap",
  deliverables: "12 final retouched campaign images\n6 social crops\n3 vertical story sequences\n1 selects contact sheet\nDelivery within 10 working days",
  usage: "Global digital, social, press and retail usage for twelve months from first publication. Paid media and extensions to be agreed in writing.",
  contact: "Jordan Lee\nCreative Producer\njordan@studio.example\n+1 310 555 0184",
};

const sectionTitles: Partial<Record<SectionType, string>> = {
  styling: "Styling / Wardrobe Brief",
  collection: "Collection 001.",
  production: "Location",
  contact: "Contacts",
  photography: "Photography Brief",
  hair: "Hair Brief",
  makeup: "Makeup Brief",
};

const template = (
  id: string,
  label: string,
  blurb: string,
  previewStyle: Exclude<TemplatePreviewStyle, "blank">,
  imageUrl: string,
  theme: PageTheme,
  fontPair: string,
  coverStyle: BriefDesign["coverStyle"],
  title: string,
  subtitle: string,
  idea: string,
  intro: string,
): BriefTemplate => ({
  id,
  label,
  blurb,
  kind: "photography",
  areas: ["concept", "photography", "styling", "hair_makeup", "casting", "production"],
  sections: PHOTOGRAPHY_BRIEF_SECTIONS,
  design: { coverStyle, orientation: "landscape", fontPair, templateId: id, layoutVersion: 2, theme },
  sample: {
    title,
    subtitle,
    idea,
    intro,
    imageUrl,
    assetKey: `template:${id}`,
    previewStyle,
    sectionCopy,
    sectionTitles,
    project: { client: "Atelier No. 7", project: "Autumn Image Campaign", dates: "October 12-13, 2026", location: "Los Angeles, CA" },
  },
});

export const BRIEF_TEMPLATES: BriefTemplate[] = [
  template("blue_horizon", "Open Air", "Luminous photography, coastal color and generous editorial space.", "horizon", focusAsset(blueHorizonAsset.url), { bg: "#F8FBFD", text: "#17212B" }, "open-air", "gallery", "Open Air", "Photography brief / Autumn 2026", "A luminous study of confidence, clarity and movement against an uninterrupted horizon.", "We create image-led campaigns that balance commercial clarity with an editorial point of view. Our process is collaborative, precise and built around the strongest possible frame."),
  template("burnished_motion", "Burnished Motion", "Cinematic warmth, directional movement and campaign-scale typography.", "motion", focusAsset(burnishedMotionAsset.url), { bg: "#F0E7DB", text: "#211B18" }, "editorial", "statement", "Momentum", "Campaign treatment / Resort 2026", "A campaign shaped by movement at the edge of daylight, where the body becomes a graphic line against the horizon.", "Our work brings disciplined production to emotionally charged imagery. We build each shoot around movement, timing and a clear visual thesis."),
  template("nocturne", "Nocturne", "Low-light luxury, dramatic restraint and a finely paced treatment rhythm.", "nocturne", focusAsset(nocturneAsset.url), { bg: "#171310", text: "#F2E8DD" }, "classic", "statement", "After Dark", "Photography brief / Evening study", "A study in silhouette, texture and controlled mystery, using darkness as material rather than absence.", "We specialize in atmospheric fashion imagery with exacting control of tone, texture and finish. Every decision serves the emotional temperature of the final picture."),
  template("sleek_monochrome", "Sleek Monochrome", "Sculptural black and white with a precise modernist grid.", "monochrome", focusAsset(sleekMonochromeAsset.url), { bg: "#FAFAFA", text: "#151515" }, "modern", "editorial", "Sleek", "Editorial photography brief", "An exacting monochrome portrait study where proportion, texture and profile carry the entire story.", "We make concise visual systems for fashion and culture. Our approach combines rigorous art direction, thoughtful casting and calm, efficient production."),
];

export function templateSystem(templateId: string | undefined): EditorialSystem | undefined {
  return templateId ? EDITORIAL_SYSTEMS[templateId] : undefined;
}

export function templatePageLayout(template: BriefTemplate | null, type: SectionType, _index: number): PageLayoutMap {
  if (!template?.sample) return {};
  const layout = templatePageRecipe(template.id, type);
  if (!layout) return {};
  return {
    __preview__: { header: layout.header, body: layout.body },
    __preview_images__: Object.fromEntries((layout.imageSlots || (layout.image ? [layout.image] : [])).map((value, slot) => [`image-slot-${slot}`, value])),
  };
}

function fixedLayouts(templateId: string): PageLayoutMap {
  const cover = templatePageRecipe(templateId, "cover");
  const intro = templatePageRecipe(templateId, "intro");
  const concept = templatePageRecipe(templateId, "concept");
  if (!cover || !intro || !concept) return {};
  const coverTitleKey = templateId === "burnished_motion" || templateId === "nocturne" ? "statement-title-v4" : "header";
  return {
    cover: { [coverTitleKey]: cover.header, ...(cover.image ? { image: cover.image } : {}), contact: cover.body },
    intro: { body: intro.body, ...(intro.image ? { image: intro.image } : {}) },
    concept: { masthead: concept.header, idea: concept.body, ...(concept.image ? { strip: concept.image } : {}), facts: placed(65, 8, 28, 23, 3) },
  };
}

export function applyTemplate(doc: BriefDoc, templateValue: BriefTemplate): BriefDoc {
  return applyTemplateForKind(doc, templateValue, templateValue.kind, templateValue.sections);
}

export function applyTemplateForKind(doc: BriefDoc, templateValue: BriefTemplate, kind: BriefKind, selected: SectionType[]): BriefDoc {
  const definition = kindDef(kind);
  const sample = templateValue.sample;
  const openAirCopy: Partial<Record<SectionType, string>> = templateValue.id === "blue_horizon"
    ? { collection: "Spring / Summer 26\nA contact sheet of the season: open silhouettes, soft coastal light and a restrained palette carried across every look." }
    : {};
  const baseSections = selected.map((type, position) => ({ id: newId("sec"), type, position, body: openAirCopy[type] || sample?.sectionCopy[type] || "", heading: sample?.sectionTitles?.[type] }));
  // Reference imagery runs across two spreads: the primary grid and a second mood page.
  const secondReferenceIds = new Set<string>();
  const sections: typeof baseSections = [];
  baseSections.forEach((section) => {
    sections.push(section);
    if (sample && section.type === "references") {
      const extraId = newId("sec");
      secondReferenceIds.add(extraId);
      sections.push({ id: extraId, type: "references" as SectionType, position: 0, body: "", heading: "Reference Images" });
    }
  });
  // The second reference spread ("Reference Images") follows the Photography Brief page.
  secondReferenceIds.forEach((id) => {
    const from = sections.findIndex((section) => section.id === id);
    const anchor = sections.findIndex((section) => section.type === "references" && !secondReferenceIds.has(section.id));
    if (from < 0 || anchor < 0) return;
    const [moved] = sections.splice(from, 1);
    sections.splice(Math.min(anchor + 2, sections.length), 0, moved);
  });
  sections.forEach((section, position) => { section.position = position; });
  const placeholderUrl = sample?.imageUrl || blueHorizonAsset.url;
  const placeholderPath = sample?.assetKey || "template:blue_horizon";
  const pageLayouts: PageLayoutMap = sample ? { ...fixedLayouts(templateValue.id) } : {};
  const imageSlots: Record<string, number> = {};
  const pageThemes: Record<string, PageTheme> = sample
    ? Object.fromEntries(PHOTOGRAPHY_TEMPLATE_PAGES.map((key) => [key, templatePageTheme(templateValue.id, key)]))
    : { ...(templateValue.design.pageThemes || {}) };
  const references = sample ? [
    { id: newId("ref"), area: "cover", url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 45) },
    { id: newId("ref"), area: "intro", url: sample.imageUrl, path: sample.assetKey, crop: crop(55, 45, 1.08) },
    { id: newId("ref"), area: "overall", url: sample.imageUrl, path: sample.assetKey, crop: crop(45, 50, 1.12), slotIndex: 0 },
    { id: newId("ref"), area: "title", url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 36, 1.05) },
    // The contents and project details pages open with their own placeholder image.
    { id: newId("ref"), area: "contents", url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 50) },
    { id: newId("ref"), area: "project-details", url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 50) },
  ] : [];

  sections.forEach((section, index) => {
    const pageKey = (secondReferenceIds.has(section.id) ? "references2" : section.type) as TemplatePageKey;
    const layout = templatePageRecipe(templateValue.id, pageKey);
    if (!layout) return;
    pageLayouts[section.id] = { header: layout.header, body: layout.body };
    const imageLayouts = layout.imageSlots || (layout.image ? [layout.image] : []);
    if (imageLayouts.length) pageLayouts[`${section.id}-images`] = Object.fromEntries(imageLayouts.map((value, slot) => [`image-slot-${slot}`, value]));
    const theme = templatePageTheme(templateValue.id, pageKey);
    if (theme.bg || theme.text) pageThemes[section.id] = theme;
    if (!sample || !VISUAL_SECTIONS.includes(section.type) || imageLayouts.length === 0) return;
    if (imageLayouts.length > 1) {
      imageSlots[section.id] = imageLayouts.length;
      for (let slotIndex = 0; slotIndex < imageLayouts.length; slotIndex += 1) references.push({ id: newId("ref"), area: `page_${section.id}`, url: placeholderUrl, path: placeholderPath, crop: crop(50, 50), slotIndex });
    } else {
      imageSlots[section.id] = 1;
      // The Location page leads with Focus's own studio photograph rather than the template portrait.
      const productionImage = section.type === "production";
      references.push({
        id: newId("ref"),
        area: `page_${section.id}`,
        url: productionImage ? focusAsset(locationStudioAsset.url) : placeholderUrl,
        path: productionImage ? "template:location_studio" : placeholderPath,
        crop: productionImage ? crop(50, 50) : crop(35 + (index % 4) * 10, 40 + (index % 3) * 8, 1.04),
        slotIndex: 0,
      });
    }
  });

  const baseDesign = { ...defaultDesign(), ...doc.design, ...templateValue.design };
  const project = sample?.project;
  const cover = project ? [
    { id: newId("cov"), label: "Client", value: project.client, type: "text" as const },
    { id: newId("cov"), label: "Project", value: project.project, type: "text" as const },
    { id: newId("cov"), label: "Shoot dates", value: project.dates, type: "text" as const },
    { id: newId("cov"), label: "Location", value: project.location, type: "text" as const },
  ] : doc.cover;
  return {
    ...doc,
    kind,
    areas: templateValue.areas.length ? templateValue.areas : definition.areas,
    title: sample?.title || doc.title,
    coverSubtitle: sample?.subtitle,
    idea: (sample && templateValue.id === "blue_horizon" ? "Soft coastal light, generous negative space and a restrained palette carry the campaign. Every frame is composed to feel effortless while keeping the talent and the product unmistakably clear." : sample?.idea) || doc.idea,
    cover,
    design: {
      ...baseDesign,
      orientation: "landscape",
      templateId: sample ? templateValue.id : undefined,
      layoutVersion: sample ? 2 : undefined,
      intro: { ...baseDesign.intro, enabled: Boolean(sample) || baseDesign.intro.enabled, heading: "About Us", body: sample?.intro || baseDesign.intro.body },
      end: { ...baseDesign.end, enabled: true, heading: "Thank You", message: "We look forward to making this together." },
      hiddenPages: sample ? Array.from(new Set([...baseDesign.hiddenPages.filter((key) => key !== "concept" && key !== "contents" && key !== "project-details"), "shoot", "talent"])) : baseDesign.hiddenPages,
      // The full opening order, mixing the fixed pages and the section pages:
      // Cover, Contents, Project details, Intro, Concept, Collection, Title, then the rest.
      pageOrder: sample ? [
        "cover", "contents", "project-details", "intro", "concept",
        ...sections.filter((section) => section.type === "collection").map((section) => section.id),
        "title",
        ...sections.filter((section) => section.type !== "collection").map((section) => section.id),
      ] : baseDesign.pageOrder,
      imageSlots: { ...(baseDesign.imageSlots || {}), ...imageSlots },
      pageThemes,
    },
    sections,
    references,
    pageLayouts,
    pageText: {
      ...(doc.pageText || {}),
      concept: templateValue.id === "blue_horizon"
        ? { title: "A luminous study of confidence, clarity and movement against an uninterrupted horizon." }
        : { title: "The Brief" },
      ...(templateValue.id === "blue_horizon" ? {
        title: { title: "Open Air", subtitle: "Light / Space / Movement" },
      } : {}),
    },
  };
}

/** Existing documents retain their stamped geometry and are never overwritten by a later template release. */
export function upgradeStarterTemplateLayout(doc: BriefDoc): BriefDoc {
  // Template documents gain the Title page image automatically; the page itself renders for every template document.
  if (doc.design.templateId && !doc.references.some((reference) => (reference.area || "overall") === "title")) {
    const sample = BRIEF_TEMPLATES.find((templateValue) => templateValue.id === doc.design.templateId)?.sample;
    if (sample) {
      doc = {
        ...doc,
        references: [...doc.references, { id: newId("ref"), area: "title", url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 36, 1.05) }],
      };
    }
  }
  // Older template documents gain the contents and project details placeholder images.
  if (doc.design.templateId) {
    const sample = BRIEF_TEMPLATES.find((templateValue) => templateValue.id === doc.design.templateId)?.sample;
    if (sample) {
      const missing = (["contents", "project-details"] as const).filter(
        (area) => !doc.references.some((reference) => reference.area === area),
      );
      if (missing.length) {
        doc = {
          ...doc,
          references: [
            ...doc.references,
            ...missing.map((area) => ({ id: newId("ref"), area, url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 50) })),
          ],
        };
      }
    }
  }
  // Template documents gain a second, mood-grid references spread straight after the first.
  if (doc.design.templateId && doc.sections.filter((section) => section.type === "references").length === 1) {
    const recipe = templatePageRecipe(doc.design.templateId, "references2");
    const sample = BRIEF_TEMPLATES.find((templateValue) => templateValue.id === doc.design.templateId)?.sample;
    if (recipe) {
      const id = newId("sec");
      const firstIndex = doc.sections.findIndex((section) => section.type === "references");
      const sections = [...doc.sections];
      sections.splice(firstIndex + 1, 0, { id, type: "references", position: firstIndex + 1, heading: "References", body: "" });
      const imageLayouts = recipe.imageSlots || [];
      const extraReferences = sample
        ? imageLayouts.map((_, slotIndex) => ({ id: newId("ref"), area: `page_${id}`, url: sample.imageUrl, path: sample.assetKey, crop: crop(50, 50), slotIndex }))
        : [];
      doc = {
        ...doc,
        sections: sections.map((section, position) => ({ ...section, position })),
        references: [...doc.references, ...extraReferences],
        pageLayouts: {
          ...(doc.pageLayouts || {}),
          [id]: { header: recipe.header, body: recipe.body },
          [`${id}-images`]: Object.fromEntries(imageLayouts.map((layout, slotIndex) => [`image-slot-${slotIndex}`, layout])),
        },
        design: {
          ...doc.design,
          imageSlots: { ...(doc.design.imageSlots || {}), [id]: imageLayouts.length },
          pageThemes: { ...(doc.design.pageThemes || {}), [id]: recipe.theme },
        },
      };
    }
  }
  if (doc.design.templateId === "sleek_monochrome" && !doc.sections.some((section) => section.type === "collection")) {
    const recipe = templatePageRecipe("sleek_monochrome", "collection");
    if (!recipe) return doc;
    const id = newId("sec");
    const stylingIndex = doc.sections.findIndex((section) => section.type === "styling");
    const insertionIndex = stylingIndex >= 0 ? stylingIndex + 1 : doc.sections.length;
    const sections = [...doc.sections];
    sections.splice(insertionIndex, 0, {
      id,
      type: "collection",
      position: insertionIndex,
      heading: "COLLECTION 001.",
      body: "Spring / Summer 26\nThis collection is shaped by calm silhouettes, tactile structure and an exact monochrome palette.",
      subtitle: "Limited Collection\n2026",
    });
    const sample = BRIEF_TEMPLATES.find((templateValue) => templateValue.id === "sleek_monochrome")?.sample;
    const imageLayouts = recipe.imageSlots || [];
    const references = sample
      ? imageLayouts.map((_, slotIndex) => ({ id: newId("ref"), area: `page_${id}`, url: sample.imageUrl, path: sample.assetKey, crop: crop(28 + slotIndex * 5, 35 + (slotIndex % 3) * 12, 1.04), slotIndex }))
      : [];
    return {
      ...doc,
      sections: sections.map((section, position) => ({ ...section, position })),
      references: [...doc.references, ...references],
      pageLayouts: {
        ...(doc.pageLayouts || {}),
        [id]: { header: recipe.header, body: recipe.body },
        [`${id}-images`]: Object.fromEntries(imageLayouts.map((layout, slotIndex) => [`image-slot-${slotIndex}`, layout])),
      },
      design: {
        ...doc.design,
        imageSlots: { ...(doc.design.imageSlots || {}), [id]: 13 },
        pageThemes: { ...(doc.design.pageThemes || {}), [id]: recipe.theme },
      },
    };
  }
  return doc;
}

const legacyAssets: Record<string, string> = {
  "template:location_studio": focusAsset(locationStudioAsset.url),
  "template:redline": legacyRedline,
  "template:tidal": legacyTidal,
  "template:beauty": legacyBeauty,
  "template:studio_day": legacyStudio,
};

export function templateAssetUrl(path: string): string | undefined {
  return BRIEF_TEMPLATES.find((item) => item.sample?.assetKey === path)?.sample?.imageUrl || legacyAssets[path];
}